import React, { useEffect, useMemo, useState } from 'react'
import './PropertiesPage.css'
import MenuBar from '../components/MenuBar'
import Header from '../components/Header'
import LoadingSpinner from '../components/placeholders/LoadingSpinner'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import CircularProgress from '@mui/material/CircularProgress'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { FaCheckCircle, FaExclamationTriangle, FaMapMarkedAlt, FaSearch, FaTimesCircle } from 'react-icons/fa'
import { GoDatabase } from 'react-icons/go'
import { MdRadioButtonUnchecked } from 'react-icons/md'
import { RiListCheck } from 'react-icons/ri'
import { RiRoadMapLine } from 'react-icons/ri'
import { toast } from 'react-toastify'
import Slider from '../components/Slider'
import PopupDropdown from '../components/PopupDropdown'
import Button from '../components/UI/Button'
import httpClient from '../components/helpers/httpClient'
import {
  formatPropertyIdentifier,
  normalizeCapakey,
  normalizePartition,
  parsePropertyIdentifier,
} from '../components/helpers/propertyIdentifier'
import { updateFilters, updatePropertiesList } from '../store/propertiesPage'
import PropertiesMapView from '../components/properties/PropertiesMapView'

const PAGE_SIZE = 20
const VALIDATION_BATCH_SIZE = 10
const MAP_CENTER_PRESETS = [
  { label: 'Kortrijk', center: [50.8273907, 3.2631452], zoom: 16 },
  { label: 'Gent', center: [51.0543422, 3.7174243], zoom: 15 },
  { label: 'Brugge', center: [51.209348, 3.2246995], zoom: 15 },
  { label: 'Antwerpen', center: [51.2194475, 4.4024643], zoom: 15 },
  { label: 'Brussel', center: [50.8465573, 4.351697], zoom: 15 },
]

const createEmptyPropertyValidation = () => ({
  status: 'unvalidated',
  message: '',
  parcel: null,
  normalized_capakey: '',
  validation_source_status: null,
  suggested_property_updates: {
    cadastral_nr: '',
    location: null,
  },
})

const createEmptyCapakeyLookupCriteria = () => ({
  street_name: '',
  house_number: '',
  box_number: '',
  postal_code: '',
  municipality_name: '',
})

const createEmptyBatchSummary = () => ({
  processed: 0,
  total: 0,
  validated: 0,
  not_found: 0,
  invalid_format: 0,
  ambiguous: 0,
  service_error: 0,
  partitions_extracted: 0,
})

const getPropertyValidationLabel = (status) => {
  if (status === 'validated') return 'Validated'
  if (status === 'not_found') return 'No official match'
  if (status === 'invalid_format') return 'Invalid format'
  if (status === 'ambiguous') return 'Needs review'
  if (status === 'service_error') return 'Validation unavailable'
  return 'Not validated'
}

const getStoredPropertyDataStatus = (property) => {
  if (!property) return null

  if (typeof property.has_data === 'boolean') return property.has_data
  if (typeof property.has_historic_data === 'boolean') return property.has_historic_data
  if (typeof property.historic_data_available === 'boolean') return property.historic_data_available

  if (typeof property.data_status === 'string') {
    if (property.data_status === 'available' || property.data_status === 'has_data') return true
    if (property.data_status === 'unavailable' || property.data_status === 'no_data') return false
  }

  return null
}

const ValidationStatusIcon = ({ status, isLoading = false }) => {
  if (isLoading) {
    return (
      <span className='property-validation-icon is-loading' title='Validating'>
        <CircularProgress size={16} thickness={5} color='inherit' />
      </span>
    )
  }

  if (status === 'validated') {
    return <FaCheckCircle className='property-validation-icon is-valid' title='Validated' />
  }

  if (status === 'ambiguous') {
    return <FaExclamationTriangle className='property-validation-icon is-ambiguous' title='Needs review' />
  }

  if (status === 'not_found' || status === 'invalid_format' || status === 'service_error') {
    return <FaTimesCircle className='property-validation-icon is-invalid' title={getPropertyValidationLabel(status)} />
  }

  return <MdRadioButtonUnchecked className='property-validation-icon is-unvalidated' title='Not validated' />
}

const PropertyDataStatusIcon = ({ hasData, title }) => (
  <span className={`properties-page__data-icon ${hasData ? 'is-active' : 'is-inactive'}`} title={title}>
    <GoDatabase />
  </span>
)

const PropertyValidationPanel = ({ validation }) => {
  if (!validation) return null

  const hasParcelInfo = Boolean(validation.parcel)
  const hasMessage = Boolean(validation.message)
  if (!hasParcelInfo && !hasMessage) return null

  return (
    <div className='property-validation-panel'>
      <div className='property-validation-panel__header'>
        <div className='property-validation-panel__title-wrap'>
          <div className='property-validation-panel__title'>Official parcel match</div>
          <div className={`property-validation-pill status-${validation.status || 'unvalidated'}`}>
            <ValidationStatusIcon status={validation.status} />
            <span>{getPropertyValidationLabel(validation.status)}</span>
          </div>
        </div>
      </div>
      {hasMessage && validation.status !== 'validated' && <div className='property-validation-panel__message'>{validation.message}</div>}
      {hasParcelInfo && (
        <div className='property-validation-grid'>
          <div className='property-validation-row highlight'>
            <span className='property-validation-row__label'>Capakey</span>
            <span className='property-validation-row__value'>{validation.parcel.capakey}</span>
          </div>
          <div className='property-validation-row'>
            <span className='property-validation-row__label'>Municipality</span>
            <span className='property-validation-row__value'>{validation.parcel.municipalityName} ({validation.parcel.municipalityCode})</span>
          </div>
          <div className='property-validation-row'>
            <span className='property-validation-row__label'>Department</span>
            <span className='property-validation-row__value'>{validation.parcel.departmentName} ({validation.parcel.departmentCode})</span>
          </div>
          <div className='property-validation-row'>
            <span className='property-validation-row__label'>Section</span>
            <span className='property-validation-row__value'>{validation.parcel.sectionCode}</span>
          </div>
          <div className='property-validation-row'>
            <span className='property-validation-row__label'>Perceelnummer</span>
            <span className='property-validation-row__value'>{validation.parcel.perceelnummer}</span>
          </div>
          <div className='property-validation-row'>
            <span className='property-validation-row__label'>Grondnummer</span>
            <span className='property-validation-row__value'>{validation.parcel.grondnummer}</span>
          </div>
          <div className='property-validation-row'>
            <span className='property-validation-row__label'>Exponent</span>
            <span className='property-validation-row__value'>{validation.parcel.exponent}</span>
          </div>
          <div className='property-validation-row'>
            <span className='property-validation-row__label'>Bisnummer</span>
            <span className='property-validation-row__value'>{validation.parcel.bisnummer}</span>
          </div>
          <div className='property-validation-row'>
            <span className='property-validation-row__label'>Macht</span>
            <span className='property-validation-row__value'>{validation.parcel.macht}</span>
          </div>
          {validation.parcel.adres?.length > 0 && validation.parcel.adres.map((address, index) => (
            <div className='property-validation-row address' key={`${address}-${index}`}>
              <span className='property-validation-row__label'>{index === 0 ? 'Address' : 'Address alt.'}</span>
              <span className='property-validation-row__value'>{address}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function PropertiesPage() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const filters = useSelector((state) => state.propertiesPage.filtersProperties)
  const properties = useSelector((state) => state.propertiesPage.propertiesList)

  const [loadingProperties, setLoadingProperties] = useState(true)
  const [loadingHistoricData, setLoadingHistoricData] = useState(false)
  const [showSearchPanel, setShowSearchPanel] = useState(false)
  const [municipalities, setMunicipalities] = useState([])
  const [loadingMunicipalities, setLoadingMunicipalities] = useState(false)
  const [capakeySearchValue, setCapakeySearchValue] = useState('')
  const [lookupCriteria, setLookupCriteria] = useState(createEmptyCapakeyLookupCriteria())
  const [searchResults, setSearchResults] = useState([])
  const [searchResultsMessage, setSearchResultsMessage] = useState('')
  const [searchingCapakey, setSearchingCapakey] = useState(false)
  const [searchingAddress, setSearchingAddress] = useState(false)
  const [searchMode, setSearchMode] = useState('capakey')
  const [historicDataByCapakey, setHistoricDataByCapakey] = useState({})
  const [showMapView, setShowMapView] = useState(false)
  const [mapSearchValue, setMapSearchValue] = useState('')
  const [mapSearching, setMapSearching] = useState(false)
  const [mapCenterRequest, setMapCenterRequest] = useState(null)
  const [selectedMapPreset, setSelectedMapPreset] = useState('Kortrijk')
  const [validatingAll, setValidatingAll] = useState(false)
  const [batchValidationSummary, setBatchValidationSummary] = useState(null)
  const [batchValidationByPropertyId, setBatchValidationByPropertyId] = useState({})

  const propertiesNeedingValidation = useMemo(() => (
    properties.filter((property) => (
      (property?.cadastral_validation_status || 'unvalidated') !== 'validated'
    )).length
  ), [properties])

  const propertiesByCapakey = useMemo(() => {
    const map = new Map()
    properties.forEach((property) => {
      const normalizedCapakey = normalizeCapakey(property?.cadastral_nr)
      if (normalizedCapakey) {
        const matches = map.get(normalizedCapakey) || []
        matches.push(property)
        map.set(normalizedCapakey, matches)
      }
    })
    return map
  }, [properties])

  const getHistoricDataStatus = (capakey) => historicDataByCapakey[normalizeCapakey(capakey)] || null

  const getPropertyHasData = (property) => {
    const storedStatus = getStoredPropertyDataStatus(property)
    if (typeof storedStatus === 'boolean') return storedStatus
    return getHistoricDataStatus(property?.cadastral_nr)?.has_data === true
  }

  const fetchProperties = async () => {
    setLoadingProperties(true)

    try {
      const response = await httpClient.get(import.meta.env.VITE_API_URL + '/api/properties/get')
      if (response.status === 200) {
        dispatch(updatePropertiesList(response.data))
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load properties')
    } finally {
      setLoadingProperties(false)
    }
  }

  const fetchMunicipalities = async () => {
    setLoadingMunicipalities(true)

    try {
      const response = await httpClient.get(import.meta.env.VITE_API_URL + '/api/properties/cadastral-municipalities')
      if (response.status === 200) {
        setMunicipalities(response.data?.municipalities || [])
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Municipality lookup unavailable')
    } finally {
      setLoadingMunicipalities(false)
    }
  }

  useEffect(() => {
    fetchProperties()
    fetchMunicipalities()
  }, [])

  const fetchHistoricDataStatus = async (capakeys) => {
    const uniqueCapakeys = Array.from(new Set((capakeys || []).map(normalizeCapakey).filter(Boolean)))

    if (uniqueCapakeys.length === 0) {
      return {}
    }

    setLoadingHistoricData(true)

    try {
      const response = await httpClient.post(
        import.meta.env.VITE_API_URL + '/api/properties/historic-data-status',
        { capakeys: uniqueCapakeys }
      )

      const nextEntries = {}
      for (const item of response.data?.results || []) {
        const key = normalizeCapakey(item?.capakey)
        if (key) {
          nextEntries[key] = item
        }
      }

      setHistoricDataByCapakey((currentValue) => ({
        ...currentValue,
        ...nextEntries,
      }))

      return nextEntries
    } catch (error) {
      toast.error(error.response?.data?.message || 'Historic data lookup unavailable')
      return {}
    } finally {
      setLoadingHistoricData(false)
    }
  }

  const filteredProperties = useMemo(() => {
    const searchWord = (filters.searchWord || '').trim().toLowerCase()

    return [...properties]
      .sort((a, b) => {
        if (a?.is_active !== b?.is_active) {
          return (b?.is_active === true) - (a?.is_active === true)
        }

        return formatPropertyIdentifier(a?.cadastral_nr, a?.partition_nr)
          .localeCompare(formatPropertyIdentifier(b?.cadastral_nr, b?.partition_nr))
      })
      .filter((property) => {
        if (!searchWord) return true

        return [
          property?.cadastral_nr,
          property?.partition_nr,
          formatPropertyIdentifier(property?.cadastral_nr, property?.partition_nr),
          property?.location,
        ]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(searchWord))
      })
      .filter((property) => {
        if (!filters.hasDataOnly) return true
        return getPropertyHasData(property)
      })
      .filter((property) => {
        if (!filters.isValidatedOnly) return true
        return (property?.cadastral_validation_status || 'unvalidated') === 'validated'
      })
  }, [filters, properties, historicDataByCapakey])

  const totalPages = Math.max(1, Math.ceil(filteredProperties.length / PAGE_SIZE))
  const currentPage = Math.min(filters.page || 1, totalPages)

  useEffect(() => {
    if ((filters.page || 1) !== currentPage) {
      dispatch(updateFilters({ ...filters, page: currentPage }))
    }
  }, [currentPage])

  const paginatedProperties = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE
    return filteredProperties.slice(startIndex, startIndex + PAGE_SIZE)
  }, [currentPage, filteredProperties])

  const capakeysToHydrate = useMemo(() => {
    const sourceProperties = filters.hasDataOnly ? properties : paginatedProperties

    return Array.from(new Set(
      sourceProperties
        .filter((property) => getStoredPropertyDataStatus(property) === null)
        .map((property) => normalizeCapakey(property?.cadastral_nr))
        .filter((capakey) => capakey && !historicDataByCapakey[capakey])
    ))
  }, [filters.hasDataOnly, historicDataByCapakey, paginatedProperties, properties])

  useEffect(() => {
    if (capakeysToHydrate.length > 0) {
      fetchHistoricDataStatus(capakeysToHydrate)
    }
  }, [capakeysToHydrate.join('|')])

  const updateLookupCriteria = (key, value) => {
    setLookupCriteria((currentValue) => ({
      ...currentValue,
      [key]: value,
    }))
  }

  const validateCadastralNr = async (cadastralNr) => {
    const response = await httpClient.post(
      import.meta.env.VITE_API_URL + '/api/properties/validate-cadastral',
      { cadastral_nr: cadastralNr }
    )

    if (response.status === 400) {
      return {
        ...createEmptyPropertyValidation(),
        ...response.data,
      }
    }

    return response.data
  }

  const lookupCapakeyByAddress = async (criteria) => {
    return httpClient.post(
      import.meta.env.VITE_API_URL + '/api/properties/lookup-cadastral-by-address',
      criteria
    )
  }

  const buildSearchResultItems = (validation, fallbackLocation = '') => {
    const normalizedCapakey = normalizeCapakey(validation?.normalized_capakey || validation?.parcel?.capakey)
    const requestedPartition = normalizePartition(validation?.partition_nr)
    const linkedProperties = propertiesByCapakey.get(normalizedCapakey) || []
    const matchingProperties = requestedPartition
      ? linkedProperties.filter((property) => normalizePartition(property?.partition_nr) === requestedPartition)
      : linkedProperties
    const historicDataStatus = getHistoricDataStatus(normalizedCapakey)
    const resultProperties = matchingProperties.length > 0 ? matchingProperties : [null]

    return resultProperties.map((linkedProperty, index) => {
      const partitionNr = linkedProperty?.partition_nr || requestedPartition || ''
      const storedStatus = getStoredPropertyDataStatus(linkedProperty)

      return {
        key: `${normalizedCapakey || fallbackLocation}|${normalizePartition(partitionNr)}|${linkedProperty?.id || index}`,
        cadastral_nr: validation?.normalized_capakey || validation?.parcel?.capakey || '',
        partition_nr: partitionNr,
        location:
          linkedProperty?.location ||
          validation?.suggested_property_updates?.location ||
          validation?.parcel?.adres?.[0] ||
          fallbackLocation ||
          '',
        hasData: typeof storedStatus === 'boolean' ? storedStatus : historicDataStatus?.has_data === true,
        validation,
        linkedProperty,
        historicDataStatus: historicDataStatus || null,
      }
    })
  }

  const hydrateSearchResultsWithHistoricData = async (results) => {
    const capakeys = results.map((result) => result.cadastral_nr)
    const statusEntries = await fetchHistoricDataStatus(capakeys)

    return results.map((result) => {
      const status = statusEntries[normalizeCapakey(result.cadastral_nr)] || getHistoricDataStatus(result.cadastral_nr)
      const storedStatus = getStoredPropertyDataStatus(result.linkedProperty)

      return {
        ...result,
        hasData: typeof storedStatus === 'boolean' ? storedStatus : status?.has_data === true,
        historicDataStatus: status || result.historicDataStatus || null,
      }
    })
  }

  const handleOpenPropertyDetail = (propertyLike) => {
    if (!propertyLike?.hasData) return

    const searchParams = new URLSearchParams()
    if (propertyLike?.linkedProperty?.id || propertyLike?.id) {
      searchParams.set('propertyId', propertyLike.linkedProperty?.id || propertyLike.id)
    }
    if (propertyLike?.cadastral_nr) {
      searchParams.set('capakey', propertyLike.cadastral_nr)
    }

    navigate(`/properties/detail?${searchParams.toString()}`, {
      state: {
        propertyRecord: propertyLike.linkedProperty || propertyLike,
        validation: propertyLike.validation || null,
        hasData: propertyLike.hasData,
        historicDataStatus: propertyLike.historicDataStatus || null,
      },
    })
  }

  const handleOpenMappedProperty = (capakey) => {
    if (!capakey) return

    const linkedProperties = propertiesByCapakey.get(normalizeCapakey(capakey)) || []
    const linkedProperty = linkedProperties.length === 1 ? linkedProperties[0] : null
    const searchParams = new URLSearchParams()

    if (linkedProperty?.id) {
      searchParams.set('propertyId', linkedProperty.id)
    }
    searchParams.set('capakey', capakey)

    navigate(`/properties/detail?${searchParams.toString()}`, {
      state: {
        propertyRecord: linkedProperty,
        hasData: true,
        historicDataStatus: getHistoricDataStatus(capakey) || { capakey, has_data: true },
      },
    })
  }

  const handleMapPresetClick = (preset) => {
    setSelectedMapPreset(preset.label)
    setMapSearchValue(preset.label)
    setMapCenterRequest({
      center: preset.center,
      zoom: preset.zoom,
      requestedAt: Date.now(),
    })
  }

  const handleMapSearch = async () => {
    const query = mapSearchValue.trim()
    if (!query) {
      toast.error('Address or city is required')
      return
    }

    setMapSearching(true)

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=be&q=${encodeURIComponent(query)}`
      )

      if (!response.ok) {
        throw new Error('Address lookup unavailable')
      }

      const results = await response.json()
      const match = results?.[0]

      if (!match?.lat || !match?.lon) {
        toast.warning('No matching address found')
        return
      }

      setSelectedMapPreset('')
      setMapCenterRequest({
        center: [Number(match.lat), Number(match.lon)],
        zoom: match.type === 'city' || match.type === 'town' || match.type === 'municipality' ? 14 : 16,
        requestedAt: Date.now(),
      })
    } catch (error) {
      toast.error(error.message || 'Address lookup unavailable')
    } finally {
      setMapSearching(false)
    }
  }

  const handleSearchWord = (event) => {
    dispatch(updateFilters({ ...filters, searchWord: event.target.value, page: 1 }))
  }

  const handleClearSearchBar = () => {
    dispatch(updateFilters({ ...filters, searchWord: '', page: 1 }))
  }

  const handleToggleFilter = (key, value) => {
    dispatch(updateFilters({
      ...filters,
      [key]: value,
      page: 1,
    }))
  }

  const handleValidateAllProperties = async () => {
    if (validatingAll || propertiesNeedingValidation === 0) return

    setValidatingAll(true)
    setBatchValidationByPropertyId({})
    setBatchValidationSummary(createEmptyBatchSummary())

    let cursor = 0
    let aggregate = createEmptyBatchSummary()
    let completed = false

    try {
      do {
        const response = await httpClient.post(
          import.meta.env.VITE_API_URL + '/api/properties/validate-all-cadastral',
          { after_id: cursor, limit: VALIDATION_BATCH_SIZE }
        )
        const responseSummary = response.data?.summary || {}
        const batchResults = response.data?.results || []

        aggregate = {
          ...aggregate,
          total: aggregate.total || response.data?.total_eligible || propertiesNeedingValidation,
          processed: aggregate.processed + (responseSummary.processed || 0),
          validated: aggregate.validated + (responseSummary.validated || 0),
          not_found: aggregate.not_found + (responseSummary.not_found || 0),
          invalid_format: aggregate.invalid_format + (responseSummary.invalid_format || 0),
          ambiguous: aggregate.ambiguous + (responseSummary.ambiguous || 0),
          service_error: aggregate.service_error + (responseSummary.service_error || 0),
          partitions_extracted: aggregate.partitions_extracted + (responseSummary.partitions_extracted || 0),
        }

        setBatchValidationSummary(aggregate)
        setBatchValidationByPropertyId((currentValue) => ({
          ...currentValue,
          ...Object.fromEntries(batchResults.map((result) => [result.property_id, result])),
        }))

        cursor = response.data?.next_cursor || 0
      } while (cursor)

      completed = true
      if (aggregate.service_error > 0) {
        toast.warning('Validation finished with service errors. Retry affected properties later.')
      } else {
        toast.success(`Checked ${aggregate.processed} properties`)
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Bulk Capakey validation stopped. Completed results were kept.')
    } finally {
      if (aggregate.processed > 0) {
        await fetchProperties()
      }
      setValidatingAll(false)
      if (!completed && aggregate.processed === 0) {
        setBatchValidationSummary(null)
      }
    }
  }

  const handlePagination = (event) => {
    if (event.currentTarget.classList.contains('grayed-out')) return

    const type = event.currentTarget.dataset.type
    if (type === 'previous') {
      dispatch(updateFilters({ ...filters, page: Math.max(1, currentPage - 1) }))
    }
    if (type === 'next') {
      dispatch(updateFilters({ ...filters, page: Math.min(totalPages, currentPage + 1) }))
    }
  }

  const handleCapakeySearch = async () => {
    if (!capakeySearchValue.trim()) {
      toast.error('Capakey is required')
      return
    }

    setSearchingCapakey(true)
    setSearchResults([])
    setSearchResultsMessage('')

    try {
      const validation = await validateCadastralNr(capakeySearchValue)
      const enrichedResults = await hydrateSearchResultsWithHistoricData(
        buildSearchResultItems(validation)
      )

      setSearchResults(enrichedResults)
      setSearchResultsMessage(validation.message || '')

      if (validation.status === 'validated') {
        toast.success('Official parcel match found')
      } else if (validation.status === 'not_found') {
        toast.warning(validation.message)
      } else {
        toast.error(validation.message)
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Capakey validation unavailable'
      const parsedIdentifier = parsePropertyIdentifier(capakeySearchValue)
      const enrichedResults = await hydrateSearchResultsWithHistoricData(
        buildSearchResultItems({
          ...createEmptyPropertyValidation(),
          status: 'service_error',
          message,
          normalized_capakey: parsedIdentifier.cadastralNr,
          partition_nr: parsedIdentifier.partitionNr,
        })
      )

      setSearchResults(enrichedResults)
      setSearchResultsMessage(message)
      toast.error(message)
    } finally {
      setSearchingCapakey(false)
    }
  }

  const handleAddressSearch = async () => {
    setSearchingAddress(true)
    setSearchResults([])
    setSearchResultsMessage('')

    try {
      const response = await lookupCapakeyByAddress(lookupCriteria)

      if (response.status === 400) {
        setSearchResultsMessage(response.data?.message || 'Address lookup needs more details')
        toast.error(response.data?.message || 'Address lookup needs more details')
        return
      }

      const rawMatches = response.data?.matches || []
      const candidatePairs = rawMatches.flatMap((match) => (
        (match.capakey_candidates || []).map((candidate) => ({ candidate, match }))
      ))

      if (candidatePairs.length === 0) {
        setSearchResultsMessage(response.data?.message || 'No parcel linked to the matching address')
        toast.warning(response.data?.message || 'No parcel linked to the matching address')
        return
      }

      const validations = await Promise.all(candidatePairs.map(async ({ candidate, match }) => {
        try {
          const validation = await validateCadastralNr(candidate.capakey)
          return buildSearchResultItems(validation, match.address)
        } catch (error) {
          return buildSearchResultItems({
            ...createEmptyPropertyValidation(),
            status: 'service_error',
            message: error.response?.data?.message || 'Capakey validation unavailable',
            normalized_capakey: normalizeCapakey(candidate.capakey),
          }, match.address)
        }
      }))

      const flattenedValidations = validations.flat()
      const dedupedResults = Array.from(new Map(flattenedValidations.map((item) => [item.key, item])).values())
      const enrichedResults = await hydrateSearchResultsWithHistoricData(dedupedResults)

      setSearchResults(enrichedResults)
      setSearchResultsMessage(response.data?.message || '')
      toast.success(`Found ${enrichedResults.length} parcel match${enrichedResults.length === 1 ? '' : 'es'}`)
    } catch (error) {
      const message = error.response?.data?.message || 'Address lookup unavailable'
      setSearchResultsMessage(message)
      toast.error(message)
    } finally {
      setSearchingAddress(false)
    }
  }

  return (
    <div className='template properties-page'>
      <Header>
        <div className='header__actions--left'>
          <button
            className={`properties-page__header-toggle ${showSearchPanel ? 'active' : ''}`}
            onClick={() => setShowSearchPanel((currentValue) => !currentValue)}
            type='button'
          >
            <RiRoadMapLine />
            {showSearchPanel ? 'Hide Capakey Search' : 'Capakey Search'}
          </button>
          <button
            className={`properties-page__header-toggle ${showMapView ? 'active' : ''}`}
            onClick={() => setShowMapView((currentValue) => !currentValue)}
            type='button'
          >
            {showMapView ? <RiListCheck /> : <FaMapMarkedAlt />}
            {showMapView ? 'List View' : 'Map View'}
          </button>
        </div>

        {showMapView ? (
          <div className='header__filters properties-page__map-controls' id='properties__map-controls'>
            <div className='search-bar properties-page__map-search'>
              <input
                placeholder='City or address...'
                value={mapSearchValue}
                onChange={(event) => setMapSearchValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleMapSearch()
                  }
                }}
              />
              <button type='button' onClick={handleMapSearch} disabled={mapSearching} aria-label='Search map location'>
                {mapSearching ? <CircularProgress size={14} thickness={5} color='inherit' /> : <SearchIcon />}
              </button>
            </div>
            <div className='properties-page__map-presets'>
              {MAP_CENTER_PRESETS.map((preset) => (
                <button
                  type='button'
                  key={preset.label}
                  className={selectedMapPreset === preset.label ? 'active' : ''}
                  onClick={() => handleMapPresetClick(preset)}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
        <div className='header__filters' id='properties__table-btns'>
          <div className='search-bar' id='properties__search-bar--table' onClick={handleClearSearchBar}>
            <input placeholder='Search property...' value={filters.searchWord || ''} onChange={handleSearchWord} />
            {(filters.searchWord || '') === '' ? <SearchIcon /> : <ClearIcon />}
          </div>
          <div className='properties-page__slider-filter'>
            <Slider
              text='Has Data'
              defaultState={filters.hasDataOnly === true}
              state={filters.hasDataOnly === true}
              size={1.2}
              fontSize='0.8rem'
              onClickFunction={(id, newState) => handleToggleFilter('hasDataOnly', newState)}
            />
          </div>
          <div className='properties-page__slider-filter'>
            <Slider
              text='Is Validated'
              defaultState={filters.isValidatedOnly === true}
              state={filters.isValidatedOnly === true}
              size={1.2}
              fontSize='0.8rem'
              onClickFunction={(id, newState) => handleToggleFilter('isValidatedOnly', newState)}
            />
          </div>
        </div>
        )}
      </Header>

      <MenuBar />

      <div className={`template__body ${showSearchPanel && !showMapView ? 'search-open' : ''}`} id='body-properties'>
        {showMapView ? (
          <div className='body__section big properties-page__map-section'>
            <PropertiesMapView active={showMapView} centerRequest={mapCenterRequest} onOpenProperty={handleOpenMappedProperty} />
          </div>
        ) : (
        <>
          <div className='body__section big properties-page__table-section'>
          <div className='table'>
            <div className='properties-page__validation-toolbar'>
              <div className='properties-page__validation-copy'>
                <span className='properties-page__validation-title'>CAPAKEY validation</span>
                <span className='properties-page__validation-description'>
                  {propertiesNeedingValidation === 0
                    ? 'All properties validated'
                    : `${propertiesNeedingValidation} ${propertiesNeedingValidation === 1 ? 'property needs' : 'properties need'} validation`}
                </span>
              </div>
              <Button
                variant='secondary'
                size='sm'
                onClick={handleValidateAllProperties}
                disabled={validatingAll || loadingProperties || propertiesNeedingValidation === 0}
                leftIcon={validatingAll ? <CircularProgress size={14} thickness={5} color='inherit' /> : <FaCheckCircle />}
              >
                {validatingAll ? 'Validating...' : 'Validate all'}
              </Button>
            </div>
            {batchValidationSummary ? (
              <div className='properties-page__batch-summary' aria-live='polite'>
                <span className='properties-page__batch-summary-main'>
                  {validatingAll
                    ? `Validating ${batchValidationSummary.processed} of ${batchValidationSummary.total || propertiesNeedingValidation}`
                    : `${batchValidationSummary.processed} properties checked`}
                </span>
                <span>{batchValidationSummary.validated} validated</span>
                <span>{batchValidationSummary.ambiguous} ambiguous</span>
                <span>{batchValidationSummary.not_found + batchValidationSummary.invalid_format + batchValidationSummary.service_error} need attention</span>
                <span>{batchValidationSummary.partitions_extracted} partitions extracted</span>
              </div>
            ) : null}
            <div className='table__header-4 properties-table-grid'>
              <div className='table__header--field'>Cadastral ID</div>
              <div className='table__header--field'>Partition</div>
              <div className='table__header--field'>Location</div>
              <div className='table__header--field'>Validation</div>
              <div className='table__header--field'>Data</div>
            </div>

            <div className='table__rows_container'>
              {loadingProperties || (filters.hasDataOnly && loadingHistoricData && properties.length > 0) ? (
                <LoadingSpinner />
              ) : paginatedProperties.length === 0 ? (
                <div className='properties-page__empty-state'>No properties match the current filters.</div>
              ) : (
                paginatedProperties.map((property) => {
                  const hasData = getPropertyHasData(property)
                  const validationStatus = property?.cadastral_validation_status || 'unvalidated'
                  const batchValidation = batchValidationByPropertyId[property.id]
                  const displayedValidationStatus = batchValidation?.outcome || validationStatus

                  return (
                    <div
                      className={`table__row-4 properties-table-grid ${property?.is_active ? '' : 'inactive'} ${hasData ? 'is-clickable' : 'is-static'} ${batchValidation ? `batch-outcome-${batchValidation.outcome}` : ''}`}
                      key={property.id || property.cadastral_nr}
                      onClick={() => handleOpenPropertyDetail({
                        ...property,
                        hasData,
                        linkedProperty: property,
                        historicDataStatus: getHistoricDataStatus(property?.cadastral_nr),
                      })}
                      title={batchValidation?.message || (hasData ? 'Open property detail' : 'Property detail becomes available when historic data exists')}
                    >
                      <div className='table__row--field properties-page__capakey'>
                        {property.cadastral_nr}
                      </div>
                      <div className='table__row--field properties-page__partition'>
                        {property.partition_nr || '-'}
                      </div>
                      <div className='table__row--field'>{property.location || '-'}</div>
                      <div className='table__row--field'>
                        <span className='properties-page__status-cell'>
                          <ValidationStatusIcon status={displayedValidationStatus} />
                          <span>{getPropertyValidationLabel(displayedValidationStatus)}</span>
                        </span>
                      </div>
                      <div className='table__row--field'>
                        <span className='properties-page__status-cell'>
                          <PropertyDataStatusIcon hasData={hasData} title={hasData ? 'Historic data available' : 'No historic data stored'} />
                          <span>{hasData ? 'Available' : 'Unavailable'}</span>
                        </span>
                      </div>
                    </div>
                  )
                })
              )}
            </div>

            <div className='pagination'>
              <ArrowBackIosIcon className={`pagination__btn ${currentPage === 1 ? 'grayed-out' : ''}`} onClick={handlePagination} data-type='previous' />
              <ArrowForwardIosIcon className={`pagination__btn ${currentPage === totalPages ? 'grayed-out' : ''}`} onClick={handlePagination} data-type='next' />
            </div>
          </div>
        </div>

        <div className={`body__section big properties-page__search-section ${showSearchPanel ? 'expanded' : 'collapsed'}`}>
          <div className='properties-page__search-shell'>
            <div className='properties-page__search-top'>
              <div className='properties-page__search-header'>Capakey Search</div>
              <div className='properties-page__search-tabs'>
                <button
                  type='button'
                  className={`properties-page__search-tab ${searchMode === 'capakey' ? 'active' : ''}`}
                  onClick={() => setSearchMode('capakey')}
                >
                  Search by capakey
                </button>
                <button
                  type='button'
                  className={`properties-page__search-tab ${searchMode === 'address' ? 'active' : ''}`}
                  onClick={() => setSearchMode('address')}
                >
                  Search by address
                </button>
              </div>
            </div>

            <div className='properties-page__search-content'>
              {searchMode === 'capakey' ? (
                <div className='capakey-lookup-panel'>
                  <div className='capakey-lookup-panel__header'>
                    <div>
                      <div className='capakey-lookup-panel__title'>Search by capakey</div>
                    </div>
                    <Button
                      variant='secondary'
                      size='sm'
                      shape='pill'
                      onClick={handleCapakeySearch}
                      disabled={searchingCapakey || searchingAddress}
                      leftIcon={searchingCapakey ? <CircularProgress size={14} thickness={5} color='inherit' /> : <FaSearch />}
                    >
                      {searchingCapakey ? 'Searching...' : 'Search'}
                    </Button>
                  </div>

                  <div className='input_field--combo-grid'>
                    <label className='input_field__label' htmlFor='properties_capakey_search'>Capakey</label>
                    <input
                      className='input_field__input_box--text'
                      id='properties_capakey_search'
                      type='text'
                      value={capakeySearchValue}
                      onChange={(e) => setCapakeySearchValue(e.currentTarget.value)}
                      placeholder='11001B0001/00S000-P0017'
                      disabled={searchingCapakey || searchingAddress}
                    />
                  </div>
                </div>
              ) : (
                <div className='capakey-lookup-panel'>
                  <div className='capakey-lookup-panel__header'>
                    <div>
                      <div className='capakey-lookup-panel__title'>Search by official address</div>
                    </div>
                    <Button
                      variant='secondary'
                      size='sm'
                      shape='pill'
                      onClick={handleAddressSearch}
                      disabled={searchingCapakey || searchingAddress}
                      leftIcon={searchingAddress ? <CircularProgress size={14} thickness={5} color='inherit' /> : <FaSearch />}
                    >
                      {searchingAddress ? 'Searching...' : 'Search'}
                    </Button>
                  </div>

                  <div className='capakey-lookup-panel__form'>
                    <div className='input_field--combo-grid'>
                      <label className='input_field__label' htmlFor='properties_lookup_street'>Street *</label>
                      <input
                        className='input_field__input_box--text'
                        id='properties_lookup_street'
                        type='text'
                        value={lookupCriteria.street_name}
                        onChange={(e) => updateLookupCriteria('street_name', e.currentTarget.value)}
                        disabled={searchingCapakey || searchingAddress}
                      />
                    </div>
                    <div className='capakey-lookup-panel__row capakey-lookup-panel__row--compact'>
                      <div className='input_field--combo-grid'>
                        <label className='input_field__label' htmlFor='properties_lookup_house_number'>No. *</label>
                        <input
                          className='input_field__input_box--text'
                          id='properties_lookup_house_number'
                          type='text'
                          value={lookupCriteria.house_number}
                          onChange={(e) => updateLookupCriteria('house_number', e.currentTarget.value)}
                          disabled={searchingCapakey || searchingAddress}
                        />
                      </div>
                      <div className='input_field--combo-grid'>
                        <label className='input_field__label' htmlFor='properties_lookup_box_number'>Box</label>
                        <input
                          className='input_field__input_box--text'
                          id='properties_lookup_box_number'
                          type='text'
                          value={lookupCriteria.box_number}
                          onChange={(e) => updateLookupCriteria('box_number', e.currentTarget.value)}
                          disabled={searchingCapakey || searchingAddress}
                        />
                      </div>
                      <div className='input_field--combo-grid'>
                        <label className='input_field__label' htmlFor='properties_lookup_postal_code'>Postal code</label>
                        <input
                          className='input_field__input_box--text'
                          id='properties_lookup_postal_code'
                          type='text'
                          value={lookupCriteria.postal_code}
                          onChange={(e) => updateLookupCriteria('postal_code', e.currentTarget.value)}
                          disabled={searchingCapakey || searchingAddress}
                        />
                      </div>
                    </div>
                    <div className='input_field--combo-grid capakey-lookup-panel__municipality'>
                      <label className='input_field__label'>Municipality or postal code *</label>
                      <PopupDropdown
                        options={municipalities.map((municipality) => municipality.municipality_name)}
                        value={lookupCriteria.municipality_name}
                        onChange={(value) => updateLookupCriteria('municipality_name', value)}
                        title={loadingMunicipalities ? 'Loading municipalities...' : 'Select municipality'}
                        searchBar
                        disabled={loadingMunicipalities || searchingCapakey || searchingAddress}
                        clearable
                        hasBorder={false}
                        formatType='input-field'
                        width='150px'
                        padding='10px 12px'
                        inputBoxHeight='42px'
                        fontSize='0.8rem'
                      />
                    </div>
                  </div>
                </div>
              )}

              {searchResultsMessage ? <div className='properties-page__search-message'>{searchResultsMessage}</div> : null}

              <div className='properties-page__results'>
              {searchingCapakey || searchingAddress ? (
                <div className='properties-page__results-loading'>
                  <CircularProgress color='inherit' />
                </div>
              ) : searchResults.length === 0 ? (
                <div className='properties-page__empty-state'>No search results yet.</div>
              ) : (
                searchResults.map((result) => (
                  <div
                    className={`properties-page__result-card ${result.hasData ? 'is-clickable' : ''}`}
                    key={result.key}
                    onClick={() => handleOpenPropertyDetail(result)}
                  >
                    <div className='properties-page__result-card-header'>
                      <div>
                        <div className='properties-page__result-title'>
                          {formatPropertyIdentifier(result.cadastral_nr, result.partition_nr) || 'Unknown capakey'}
                        </div>
                        <div className='properties-page__result-subtitle'>{result.location || 'No location available'}</div>
                      </div>
                      <div className='properties-page__result-meta'>
                      {result.linkedProperty ? <span className='properties-page__db-pill'>In database</span> : null}
                      <span className='properties-page__status-cell'>
                        <PropertyDataStatusIcon hasData={result.hasData} title={result.hasData ? 'Historic data available' : 'No historic data stored'} />
                        <span>{result.hasData ? 'Available' : 'Unavailable'}</span>
                      </span>
                    </div>
                  </div>

                    <PropertyValidationPanel validation={result.validation} />

                    <div className='properties-page__result-actions'>
                      <Button
                        variant='secondary'
                        size='sm'
                        shape='pill'
                        disabled={!result.hasData}
                        onClick={(event) => {
                          event.stopPropagation()
                          handleOpenPropertyDetail(result)
                        }}
                      >
                        Check Data
                      </Button>
                    </div>
                  </div>
                ))
              )}
              </div>
            </div>
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  )
}

export default PropertiesPage
